/**
 * "As described in 4.2" — a reference that is still right after somebody moves section 4.
 *
 * WHAT IT REPLACES. A procedure says "see section 4.2" constantly, and today that is typed text
 * that rots silently: insert a section above and every reference in the document is now pointing
 * somewhere else, while still reading as though it means something. That is the worst kind of
 * wrong in a controlled document — confidently incorrect, with nothing to notice.
 *
 * THE NODE STORES WHO IT POINTS AT, NEVER WHAT IT SAYS. It carries an anchor and a choice of what
 * to show; the number and the title are worked out from the outline at display time, at both ends.
 * Store "4.2" in the document and you have invented a second copy of the thing that goes stale.
 *
 * AN ANCHOR IS ASSIGNED WHEN A HEADING IS FIRST REFERENCED, not when it is created. Stamping an id
 * onto every heading in every document the moment this shipped would rewrite every body, change
 * every digest, and put an entry in every history — for an attribute almost none of them would
 * ever use.
 *
 * A REFERENCE TO A HEADING THAT HAS GONE says so, in the text, rather than disappearing. A silent
 * gap is a sentence that no longer parses and nobody can see why.
 */
import { Extension, Node, mergeAttributes } from "@tiptap/core";
import { Plugin, PluginKey } from "@tiptap/pm/state";
import { Decoration, DecorationSet } from "@tiptap/pm/view";

import { MISSING, ReferenceShows, referenceText } from "./outline";

// THE RULE LIVES IN outline.ts, beside the outline it reads -- and so it can be tested without
// Jest parsing @tiptap/core. Re-exported here because this is where a reader looks for it.
export { MISSING, referenceText };
export type { ReferenceShows };

/**
 * The id a reference points at, carried on the heading.
 *
 * ONLY ON HEADINGS. A reference to an arbitrary paragraph is a bookmark, which is its own feature
 * and is not this one -- and giving every block an id would double the attributes in every
 * document for something almost none of them use.
 */
export const HeadingAnchor = Extension.create({
    name: "headingAnchor",
    addGlobalAttributes() {
        return [{
            types: ["heading"],
            attributes: {
                anchor: {
                    default: null,
                    parseHTML: (element: HTMLElement) => element.getAttribute("data-anchor"),
                    renderHTML: (attributes: any) =>
                        attributes.anchor ? { "data-anchor": attributes.anchor } : {},
                },
            },
        }];
    },
});

export const CrossReference = Node.create({
    name: "crossReference",
    group: "inline",
    inline: true,
    atom: true,
    // SELECTABLE, because somebody has to be able to click it and delete it. Not editable: its
    // text is computed, and letting it be typed into would create exactly the stale copy the whole
    // design avoids.
    selectable: true,

    addAttributes() {
        return {
            target: {
                default: null,
                parseHTML: (element) => element.getAttribute("data-xref"),
                renderHTML: (attributes) => ({ "data-xref": attributes.target }),
            },
            shows: {
                default: "number" as ReferenceShows,
                parseHTML: (element) => element.getAttribute("data-xref-shows") || "number",
                renderHTML: (attributes) => ({ "data-xref-shows": attributes.shows }),
            },
        };
    },

    parseHTML() {
        return [{ tag: "span[data-xref]" }];
    },

    renderHTML({ HTMLAttributes }) {
        // THE TEXT IS NOT RENDERED HERE. renderHTML runs once per node change and knows nothing
        // about the rest of the document, so a reference drawn here would not notice a section
        // being inserted above its target. CrossReferenceText fills it in, in one pass, after
        // every change. See below.
        return ["span", mergeAttributes(HTMLAttributes, { class: "parkour-xref" })];
    },
});

const key = new PluginKey("crossReferenceText");

/**
 * Put every reference's text on it as an attribute, and let CSS draw it.
 *
 * A DECORATION, NOT A DOM EDIT. The first version wrote the words straight into the span's
 * textContent on every update -- which is mutating the editor's DOM behind ProseMirror's back.
 * ProseMirror's observer sees the change, treats it as somebody typing, and dispatches a
 * transaction; that transaction triggers the next pass, which mutates again. The editor spent its
 * time in a loop and the page stopped responding to clicks, which is how this was found.
 *
 * So the text goes on as `data-text` and the stylesheet renders it with `content: attr(data-text)`.
 * Generated content is not in the document, cannot be typed into, is not read back by the observer,
 * and is exactly how the heading numbers are drawn a few files away.
 *
 * ONE PASS OVER THE WHOLE DOCUMENT, because what a reference reads as depends on the headings
 * AROUND it: insert a section at the top and every reference below changes, though none of their
 * own nodes did.
 */
export const CrossReferenceText = Extension.create({
    name: "crossReferenceText",

    addProseMirrorPlugins() {
        return [new Plugin({
            key,
            props: {
                decorations(state) {
                    const body = state.doc.toJSON();
                    const numbered = Boolean(state.doc.attrs?.outlineNumbering);
                    const found: any[] = [];

                    state.doc.descendants((node: any, pos: number) => {
                        if (node.type.name !== "crossReference") return;
                        const text = referenceText(body, node.attrs?.target,
                                                   node.attrs?.shows || "number", numbered);
                        found.push(Decoration.node(pos, pos + node.nodeSize, {
                            "data-text": text,
                            class: text === MISSING
                                ? "parkour-xref parkour-xref-missing" : "parkour-xref",
                        }));
                    });

                    return DecorationSet.create(state.doc, found);
                },
            },
        })];
    },
});

/**
 * Give a heading an id, if it has not got one.
 *
 * STABLE AND NEVER REUSED. The id is what a reference points at, so handing the same one to a
 * second heading would make an existing reference silently change what it means.
 */
export function ensureAnchor(editor: any, headingPos: number): string | null {
    if (!editor) return null;
    const node = editor.state.doc.nodeAt(headingPos);
    if (!node || node.type.name !== "heading") return null;
    if (node.attrs?.anchor) return node.attrs.anchor;

    const anchor = `h-${Math.random().toString(36).slice(2, 10)}`;
    const tr = editor.state.tr.setNodeMarkup(headingPos, undefined,
                                             { ...node.attrs, anchor });
    editor.view.dispatch(tr);
    return anchor;
}
